import { LocalDate } from "@js-joda/core";
import * as c from "purify-ts/Codec";
import h from "trader-hyperscript";
import { Source } from "trader.ts/types/source";
import { dynStyle, scheduleForCleanup } from "trader.ts/ui";
import { mapPartial } from "trader.ts/utils";
import type { get_students_full } from "../../../sql/classy/lessons";
import { siteid, thirdpartysellerid } from "../../../sql/classy/types";
import * as style from "../../../styles/style.css.classes";
import { renderTableDownloadLinks } from "../../client/downloadtable";
import {
  booleanFilter,
  CompositeFilterSpecs,
  Filter,
  lens,
  mkCompositeFilter,
  multiSelectBoxClassy,
  multiSelectFilter,
  numberFilter,
  stringFilter,
} from "../../client/filterbuild";
import {
  classyFiltersImpl,
  renderFiltersSaveAndLoadButtons,
} from "../../client/filtersaveloadbuttons";
import { getCurrentOrNextPeriod } from "../../domain/periods";
import {
  getFromLocalStorage,
  localStorageSpec,
  setLocalStorage,
} from "../../lib/localstorage";
import { allWeekdays, calculateAge } from "../../lib/time";
import { parseWeekday, serializeWeekday } from "../../parsing_from_ur";
import {
  ddMMyyyy,
  HHmm,
  renderCurrency,
  renderEnrollmentStatus,
  renderFirstNameLastName,
} from "../../render";
import * as routes from "../../routes";
import { renderPeriod, translateWeekday } from "../../translations_specific";
import { advancedStudentFiltersData } from "../groupmessage/client";
import { isNil } from "../../lib/istype";
import { renderRemote, textbox } from "trader.ts/ui.common";
import { match } from "ts-pattern";
import { Remote } from "trader.ts";
import { rpcIntoRemote } from "trader.ts/network.remote";

type enrollmentFiltersData = advancedStudentFiltersData;
type enrollment = Awaited<ReturnType<typeof get_students_full>>[number];
type student = Awaited<ReturnType<typeof get_students_full>>[number]["student"];

const filterLs: localStorageSpec<string> = {
  key: "classy-advanced-enrollments-filter",
  codec: c.string,
};

const showColumnsLs: localStorageSpec<string[]> = {
  key: "classy-advanced-enrollments-show-columns",
  codec: c.array(c.string),
};

const columns = [
  "link",
  "teacher",
  "student",
  "weekday",
  "time",
  "lessonduration",
  "lg_numberoflessons",
  "enrolled_numberoflessons",
  "firstlesson",
  "lastlesson",
  "status",
  "period",
  "formula",
  "student_phones",
  "student_emails",
  "instrument",
  "invoice_total",
  "invoice_left",
  "invoice_has_periodic_eps",
  "room",
  "site",
] as const;
type column = (typeof columns)[number];

function showColumn(c: column): string {
  return match(c)
    .with("link", () => "Link")
    .with("teacher", () => "Docent")
    .with("student", () => "Student")
    .with("weekday", () => "Weekdag")
    .with("time", () => "Tijdstip")
    .with("lessonduration", () => "Lesduur")
    .with("lg_numberoflessons", () => "Aantal lessen in lessenreeks")
    .with("enrolled_numberoflessons", () =>
      "Aantal lessen ingeschreven",
    )
    .with("firstlesson", () => "Eerste les")
    .with("lastlesson", () => "Laatste les")
    .with("status", () => "Status")
    .with("period", () => "Schooljaar")
    .with("formula", () => "Formule")
    .with("student_phones", () => "Student telefoon")
    .with("student_emails", () => "Student email")
    .with("instrument", () => "Cursus")
    .with("invoice_total", () => "Factuur totaal")
    .with("invoice_left", () => "Factuur nog te betalen")
    .with("invoice_has_periodic_eps", () => "Factuur is periodiek")
    .with("room", () => "Lokaal")
    .with("site", () => "Vestiging")
    .exhaustive();
}

export function render(opts: {
  data: enrollmentFiltersData;
}): HTMLElement | HTMLElement[] {
  const updateS = new Source(true);

  const enrollmentsRS: Source<Remote.Remote<enrollment[]>> = new Source({
    tag: "initial",
  });
  void rpcIntoRemote(
    routes.getAllEnrollmentsSpec,
    undefined,
    {},
    enrollmentsRS,
  );

  const enrollmentFilter = mkEnrollmentFilter(opts.data, true)();
  enrollmentFilter.observe(() => {
    updateS.set(!updateS.get());
    setLocalStorage(
      filterLs,
      JSON.stringify(enrollmentFilter.serializeState()),
    );
  });

  const fromLs = getFromLocalStorage(filterLs);
  if (fromLs) {
    enrollmentFilter.applySerializedState(JSON.parse(fromLs));
  } else {
    // TODO: this is not great, this should be typed
    const today = LocalDate.now();
    const currentPeriod = getCurrentOrNextPeriod(today, opts.data.periods);
    if (currentPeriod) {
      enrollmentFilter.applySerializedState({
        operator: "and",
        filters: [
          {
            key: "period",
            filter: {
              ids: [currentPeriod.id.toString()],
              op: "eq",
            },
          },
        ],
      });
    }
  }

  const columnsSelected = new Source([] as column[]);
  const colsFromLs = getFromLocalStorage(showColumnsLs);
  if (colsFromLs) {
    columnsSelected.set(
      columns.filter((c) => colsFromLs.includes(c as string)),
    );
  } else {
    columnsSelected.set(["link", "teacher", "student", "weekday", "time"]);
  }
  scheduleForCleanup(
    columnsSelected.observe((selected) =>
      setLocalStorage(showColumnsLs, selected),
    ),
  );
  const columnSelectButton = renderColumnSelectButtonRight(
    columns,
    columnsSelected,
    showColumn,
  );

  function showIfColumnSelected(c: column, content: HTMLElement): HTMLElement {
    return dynStyle(
      columnsSelected,
      (selected) => (selected.includes(c) ? "" : "display: none"),
      content,
    );
  }

  const buttons = renderFiltersSaveAndLoadButtons(
    classyFiltersImpl,
    enrollmentFilter,
    "Enrollment",
    "inschrijvingen",
  );

  const tableid = "classy-table-id";
  const downloadLinks = renderTableDownloadLinks({
    tableid,
    filename: "Overzicht inschrijvingen",
    replaceNewlinesWithCommas: true,
    removeDisplayNoneRows: true,
  });

  return (
    <div>
      <div className={style.card + style.card_large}>
        <h1 style="margin: -8px 0px 32px 0px; font-size: 18px;">
          {"Zoek inschrijvingen"}
        </h1>
        <article>{enrollmentFilter.render()}</article>
        <div>{buttons}</div>
      </div>
      <div className={style.card + style.card_large}>
        <div style="display: flex;">
          <div style="flex: 1"></div>
          {downloadLinks}
          <div style="margin-left: 16px">{columnSelectButton}</div>
        </div>
        <table id={tableid}>
          <tr>
            {columns.map((c) =>
              showIfColumnSelected(c, <th>{showColumn(c)}</th>),
            )}
          </tr>
          {renderRemote(enrollmentsRS, (enrollments) =>
            enrollments.map((enr) =>
              dynStyle(
                updateS,
                () =>
                  enrollmentFilter.filter(enr) === true ? "" : "display: none;",
                <tr>
                  {columns.map((c) =>
                    showIfColumnSelected(
                      c,
                      <td>
                        {match(c)
                          .with("link", () => (
                            <a
                              href={routes.lessongroup_generalRoute.link({
                                lessongroupid: enr.lessongroup.id,
                              })}
                            >
                              {"Lessenreeks"}
                            </a>
                          ))
                          .with("teacher", () =>
                            renderFirstNameLastName(enr.teacher),
                          )
                          .with("student", () =>
                            renderFirstNameLastName(enr.student),
                          )
                          .with("weekday", () =>
                            translateWeekday(
                              parseWeekday(enr.lessongroup.weekday),
                            ),
                          )
                          .with("time", () => enr.lessongroup.time.format(HHmm))
                          .with(
                            "lessonduration",
                            () =>
                              enr.lessongroup.lessonduration.toString() + "m",
                          )
                          .with(
                            "lg_numberoflessons",
                            () => enr.lessongroup.numberoflessons,
                          )
                          .with(
                            "enrolled_numberoflessons",
                            () => (enr.lessons || []).length,
                          )
                          .with(
                            "firstlesson",
                            () =>
                              enr.lessons?.first()?.date.format(ddMMyyyy) || "",
                          )
                          .with(
                            "lastlesson",
                            () =>
                              enr.lessons?.last()?.date.format(ddMMyyyy) || "",
                          )
                          .with("status", () =>
                            renderEnrollmentStatus(enr.enrollment_status, {
                              lessons: { tag: "dont_show_last_lesson" },
                              startedlessonson: enr.enrollment_startedlessonson,
                              stoppedlessonson: enr.enrollment_stoppedlessonson,
                            }),
                          )
                          .with("period", () => {
                            const p = opts.data.periods.find(
                              (p) => p.id === enr.lessongroup.startsinperiod,
                            );
                            return p ? renderPeriod(p.firstday, p.lastday) : "";
                          })
                          .with("formula", () => enr.formula.description)
                          .with("student_phones", () =>
                            (enr.student.phones || [])
                              .map((p) => p.number)
                              .join(", "),
                          )
                          .with("student_emails", () =>
                            (enr.student.emails || [])
                              .map((p) => p.email)
                              .join(", "),
                          )
                          .with("instrument", () => enr.instrument.description)
                          .with("invoice_total", () =>
                            !isNil(enr.invoice.amount_total)
                              ? renderCurrency(enr.invoice.amount_total)
                              : "",
                          )
                          .with("invoice_left", () =>
                            !isNil(enr.invoice.amount_left)
                              ? renderCurrency(enr.invoice.amount_left)
                              : "",
                          )
                          .with("invoice_has_periodic_eps", () =>
                            enr.invoice.has_periodic_eps
                              ? "Periodiek"
                              : "In een keer",
                          )
                          .with("room", () => enr.room.description || "")
                          .with("site", () => enr.site.description || "")
                          .exhaustive()}
                      </td>,
                    ),
                  )}
                </tr>,
              ),
            ),
          )}
        </table>
      </div>
    </div>
  );
}

export function mkStudentFilterImpl(
  today: LocalDate,
  data: {
    third_party_sellers: { id: thirdpartysellerid | null; name: string }[];
  },
): CompositeFilterSpecs<student> {
  return {
    age: {
      label: "Leeftijd",
      mkFilter: () =>
        lens(
          (stu: student) =>
            stu.birthday === null
              ? 0
              : calculateAge({ birthday: stu.birthday, today }),
          numberFilter({}),
        ),
    },
    city: {
      label: "Woonplaats",
      mkFilter: () => lens((stu: student) => stu.city, stringFilter({})),
    },
    firstname: {
      label: "Voornaam",
      mkFilter: () => lens((stu: student) => stu.firstname, stringFilter({})),
    },
    lastname: {
      label: "Achternaam",
      mkFilter: () => lens((stu: student) => stu.lastname, stringFilter({})),
    },
    ...(data.third_party_sellers.length >= 2 // always have one "null" element here
      ? {
          site: {
            label: "Vereniging",
            mkFilter: () =>
              lens(
                (stu: student) =>
                  stu.thirdpartysellerid === null
                    ? "null"
                    : stu.thirdpartysellerid.toString(),
                multiSelectFilter({
                  values: data.third_party_sellers.map((s) => ({
                    value: s,
                    label: s.name,
                    id: s.id === null ? "null" : s.id.toString(),
                  })),
                }),
              ),
          },
        }
      : {}),
  };
}

export function mkEnrollmentFilter(
  data: advancedStudentFiltersData,
  recursive: boolean,
): () => Filter<enrollment> {
  const today = LocalDate.now();

  return () =>
    mkCompositeFilter<enrollment>({
      teacher: {
        label: "Docent",
        mkFilter: () =>
          lens(
            (enr) => enr.teacher.id.toString(),
            multiSelectFilter({
              values: data.internalteachers.map((v) => ({
                value: v,
                label: renderFirstNameLastName(v),
                shortLabel: v.firstname,
                id: v.id.toString(),
              })),
              render: {
                maxValuesOnButtonBeforeAbbrev: 4,
              },
            }),
          ),
      },
      instrument: {
        label: "Cursus",
        mkFilter: () =>
          lens(
            (enr) => enr.instrument.id.toString(),
            multiSelectFilter({
              values: data.instruments.map((v) => ({
                value: v,
                label: v.description,
                id: v.id.toString(),
              })),
            }),
          ),
      },
      formula: {
        label: "Formule",
        mkFilter: () =>
          lens(
            (enr) => enr.formula.id.toString(),
            multiSelectFilter({
              values: data.formulas.map((v) => ({
                value: v,
                label: v.description,
                id: v.id.toString(),
              })),
            }),
          ),
      },
      weekday: {
        label: "Weekdag",
        mkFilter: () =>
          lens(
            (enr) => enr.lessongroup.weekday,
            multiSelectFilter({
              values: allWeekdays.map((v) => ({
                value: v,
                label: translateWeekday(v),
                id: serializeWeekday(v),
              })),
            }),
          ),
      },
      period: {
        label: "Schooljaar",
        mkFilter: () =>
          lens(
            (enr) => enr.lessongroup.startsinperiod.toString(),
            multiSelectFilter({
              values: data.periods.map((v) => ({
                value: v,
                label: renderPeriod(v.firstday, v.lastday),
                id: v.id.toString(),
              })),
            }),
          ),
      },
      invoice: {
        label: "Factuur",
        mkFilter: () =>
          lens(
            (enr) => enr.invoice,
            mkCompositeFilter<enrollment["invoice"]>({
              amount_total: {
                label: "Totaal bedrage",
                mkFilter: () =>
                  lens(
                    (inv) => (inv.amount_total?.intValue || 0) / 100,
                    numberFilter({}),
                  ),
              },
              amount_left: {
                label: "Nog te betalen",
                mkFilter: () =>
                  lens(
                    (inv) => (inv.amount_left?.intValue || 0) / 100,
                    numberFilter({}),
                  ),
              },
              has_periodic_eps: {
                label: "Periodiek",
                mkFilter: () =>
                  lens(
                    (inv) => inv.has_periodic_eps || false,
                    booleanFilter({}),
                  ),
              },
            }),
          ),
      },
      student: {
        label: "Student",
        mkFilter: () =>
          lens(
            (enr) => enr.student,
            mkCompositeFilter(
              mkStudentFilterImpl(today, {
                third_party_sellers: data.third_party_sellers,
              }),
            ),
          ),
      },
      ...(data.sites.length > 1
        ? {
            site: {
              label: "Vestiging",
              mkFilter: () =>
                lens(
                  (enr) =>
                    enr.site.id === null ? "null" : enr.site.id.toString(),
                  multiSelectFilter({
                    values: [
                      { description: "Geen", id: -1 as siteid },
                      ...mapPartial(data.sites, (s) =>
                        s.id !== null
                          ? {
                              description: s.description,
                              id: s.id,
                            }
                          : null,
                      ),
                    ].map((v) => ({
                      value: v,
                      label: v.description,
                      id: v.id.toString(),
                    })),
                  }),
                ),
            },
          }
        : {}),
      ...(recursive
        ? {
            group: {
              label: "FilterGroep",
              mkFilter: () => {
                const f: Filter<enrollment> = mkEnrollmentFilter(data, false)();
                return {
                  ...f,
                  render: () => <div>{f.render()}</div>,
                };
              },
            },
          }
        : {}),
    });
}

export function renderColumnSelectButtonRight<Column extends string>(
  columns: readonly Column[],
  columnsSelectedS: Source<Column[]>,
  showColumn: (c: Column) => string,
) {
  const sb = multiSelectBoxClassy({
    source: columnsSelectedS,
    options: columns.map((c) => ({
      value: c,
      label: showColumn(c),
    })),
    render: {
      popupClass: style.popup_dropdown + style.multiselect_dropdown,
      popupStyle: "min-width: 360px; right: 0px; top: 28px;",
      checkboxClass: style.checkbox + style.input_like,
      searchbox: (searchS) =>
        textbox({
          source: searchS,
          class: style.input_like,
        }),
      button: (onclick, selected) => {
        return (
          <button onclick={onclick} className={style.select_like}>
            {"%n kolommen getoond".replace(
              "%n",
              selected.length.toString(),
            )}
          </button>
        );
      },
    },
  });
  return (
    <div style="text-align: right;">
      <div style="display: inline-block;">{sb}</div>
    </div>
  );
}
